import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import styled from '@emotion/styled';
import { FaTrash } from 'react-icons/fa';
import { MdUpload } from 'react-icons/md';
import maverickPortalhttp from '../../utils/webService/webService';
import maverickPortalApiEndPoint from '../../config/apiEndPoint';
import { clearVideoUploadSession } from '../../utils/videoUploadSession';
import { formatRemainingUploadTime } from '../../utils/multipartVideoUpload';
 
export const VideoUploadContainer = styled.div`
  margin-top: 1rem;
  border-radius: 12px;
  padding: 12px;
  background: #fff;
  display: flex;
  flex-direction: column;
  align-items: center;
`;
 
export const SuccessRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  margin-bottom: 8px;
`;
 
export const SuccessMessage = styled.span`
  color: #9ebe21;
  font-weight: 500;
  font-size: 13px;
  display: flex;
  align-items: center;
  gap: 5px;
  @media (min-width: 600px) {
    font-size: 15px;
  }
`;
 
export const DeleteButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: #888;
  margin-left: 12px;
  display: flex;
  align-items: center;
  padding: 5px;
  border-radius: 4px;
  transition: all 0.2s ease-in-out;
  &:hover {
    color: #333;
    background-color: #f0f0f0;
  }
  svg {
    font-size: 1.2em;
  }
`;
 
export const UploadButton = styled.button`
  background: #fff;
  color: #9ebe21;
  border: 2px solid #9ebe21;
  font-weight: bold;
  border-radius: 6px;
  padding: 10px 26px;
  cursor: pointer;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s ease-in-out;
  &:hover {
    background: #f6fbe9;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
  &:active {
    transform: translateY(1px);
  }
`;
 
export const MediaPreview = styled.div`
  width: 100%;
  max-width: 500px;
  margin-bottom: 15px;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  video {
    width: 100%;
    height: auto;
    display: block;
  }
`;
 
const Spinner = styled.div`
  border: 4px solid #f3f3f3;
  border-top: 4px solid #9ebe21;
  border-radius: 50%;
  width: 16px;
  height: 16px;
  animation: spin 1s linear infinite;
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;
 
const VideoResponseUpload = ({
  question,
  mediaUrls,
  setMediaUrls,
  selectedFiles,
  setSelectedFiles,
  handleAnswerChange,
  uploadVideo,
  preAssignedVideoUrl,
  setPreAssignedVideoUrls,
  videoUploadScreenSuccess,
  setVideoUploadSuccess,
  successMsg,
  onRequestStartRecording,
  disabled = false,
  savedUploadProgress = 0,
  uploadStage: uploadStageProp,
  remainingUploadSeconds: remainingUploadSecondsProp = null,
  uploadActionError = '',
  connectionMessageStage = null,
  canResumeVideoUpload = false,
  onResumeVideoUpload,
  onDeleteIncompleteVideoUpload,
  onClearUploadActionError,
}) => {
  const questionId = question.question_ID;
  const logPrefix = `[VideoResponseUpload][Q${questionId}]`;
  const [localFile, setLocalFile] = useState(null);
  const [localPreview, setLocalPreview] = useState(null);
  const currentPreviewRef = useRef(null);
  const videoElementRef = useRef(null);
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [checkFileApiMessageStage, setCheckFileApiMessageStage] = useState(null);
  const [uploadProgressFraction, setUploadProgressFraction] = useState(0);
  const [remainingUploadSeconds, setRemainingUploadSeconds] = useState(null);
  const [uploadStage, setUploadStage] = useState('preparing');
  const [uploadErrorMessage, setUploadErrorMessage] = useState('');
  const uploadErrorTimeoutRef = useRef(null);

  const clearUploadErrorMessage = useCallback(() => {
    if (uploadErrorTimeoutRef.current) {
      clearTimeout(uploadErrorTimeoutRef.current);
      uploadErrorTimeoutRef.current = null;
    }
    setUploadErrorMessage('');
  }, []);

  const showTempUploadErrorMessage = useCallback(message => {
    if (uploadErrorTimeoutRef.current) {
      clearTimeout(uploadErrorTimeoutRef.current);
    }
    setUploadErrorMessage(message);
    uploadErrorTimeoutRef.current = setTimeout(() => {
      setUploadErrorMessage('');
      uploadErrorTimeoutRef.current = null;
    }, 5000);
  }, []);
 
  const currentVideoUrl = useMemo(
    () => preAssignedVideoUrl || mediaUrls[questionId],
    [preAssignedVideoUrl, mediaUrls, questionId]
  );
 
  useEffect(() => {
    return () => {
      if (uploadErrorTimeoutRef.current) {
        clearTimeout(uploadErrorTimeoutRef.current);
      }
      if (localPreview) {
        // Delay revocation slightly to ensure video element is fully unmounted
        const urlToRevoke = localPreview;
        setTimeout(() => {
          try {
            URL.revokeObjectURL(urlToRevoke);
            // console.log(`${logPrefix} cleanup revoked preview URL`, { urlToRevoke });
          } catch (error) {
            // URL might already be revoked, ignore error
            // console.warn(`${logPrefix} cleanup revocation failed (likely already revoked)`, { urlToRevoke, error });
          }
        }, 100);
      }
    };
  }, [localPreview, logPrefix]);
 
  useEffect(() => {
    currentPreviewRef.current = localPreview;
    if (localPreview) {
      // console.log(`${logPrefix} localPreview updated`, { localPreview });
    } else {
      // console.log(`${logPrefix} localPreview cleared`);
    }
  }, [localPreview, logPrefix]);

  // iOS-specific video loading fix
  useEffect(() => {
    if (!localPreview || !videoElementRef.current) return;
    
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || 
                  (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    
    if (isIOS) {
      const videoElement = videoElementRef.current;
      // On iOS, ensure video element loads properly after preview URL is set
      if (videoElement && videoElement.src === localPreview) {
        // Force reload if video hasn't started loading
        if (videoElement.readyState === 0 || videoElement.readyState === 1) {
          // console.log(`${logPrefix} iOS: forcing video load`, { 
          //   localPreview,
          //   readyState: videoElement.readyState 
          // });
          setTimeout(() => {
            if (videoElement.src === localPreview) {
              videoElement.load();
            }
          }, 100);
        }
      }
    }
  }, [localPreview, logPrefix]);
 
  // After upload (manual or auto-resume), parent sets preAssignedVideoUrl.
  // Clear local submit/preview state so success UI can show.
  useEffect(() => {
    if (!preAssignedVideoUrl) return;
    setSubmitting(false);
    setCheckFileApiMessageStage(null);
    setLocalFile(() => null);
    setLocalPreview(() => null);
  }, [preAssignedVideoUrl]);
 
  useEffect(() => {
    // console.log(`${logPrefix} state snapshot`, {
    //   localFile,
    //   localPreview,
    //   currentVideoUrl,
    //   submitting,
    //   deleting,
    //   videoUploadScreenSuccess,
    //   disabled,
    // });
  }, [
    localFile,
    localPreview,
    currentVideoUrl,
    submitting,
    deleting,
    videoUploadScreenSuccess,
    disabled,
  ]);
 
  useEffect(() => {
    if (localPreview || !selectedFiles) return;
    const existingFile = selectedFiles[questionId];
    if (!(existingFile instanceof File)) return;
    const restoredUrl = URL.createObjectURL(existingFile);
    // console.log(`${logPrefix} restoring preview from selectedFiles`, {
    //   existingFile,
    //   restoredUrl,
    // });
    setLocalFile(existingFile);
    setLocalPreview(restoredUrl);
    return () => {
      // console.log(`${logPrefix} cleanup restored preview`, { restoredUrl });
      URL.revokeObjectURL(restoredUrl);
    };
  }, [selectedFiles, questionId, localPreview]);
 
  const handleDelete = async () => {
    // const connection = navigator.connection;
    // const connectionLooksDown =
    //   Boolean(connection) && (connection.metered || (typeof connection.downlink === 'number' && connection.downlink < 1));
    if (!navigator.onLine) {
      alert('Network is down. Please try connecting to another network');
      return;
    }
    setDeleting(true);
    try {
      const user_ID = localStorage.getItem('user_id');
      const payload = { user_ID, question_ID: questionId };
 
      await maverickPortalhttp.makeRequest(
        maverickPortalApiEndPoint.deleteMedia.url,
        maverickPortalApiEndPoint.deleteMedia.methodType,
        payload
      );
      clearVideoUploadSession(questionId).catch(() => {});
 
      setMediaUrls((prev) => {
        const newState = { ...prev };
        delete newState[questionId];
        return newState;
      });
      setPreAssignedVideoUrls((prev) => {
        const newState = { ...prev };
        delete newState[questionId];
        return newState;
      });
      setSelectedFiles((prev) => {
        const newState = { ...prev };
        delete newState[questionId];
        return newState;
      });
 
      handleAnswerChange(questionId, null);
      // Clear parent success flag so success UI does not stick after trash.
      setVideoUploadSuccess?.(prev => {
        const newState = { ...prev };
        delete newState[questionId];
        return newState;
      });
      setLocalFile(null);
      setLocalPreview(null);
      // Clear video element ref
      videoElementRef.current = null;
    } catch (error) {
      // console.error('Error deleting video:', error);
      alert('Failed to delete the video. Please try again.');
    } finally {
      setDeleting(false);
    }
  };
 
  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('video/')) {
      // console.warn(`${logPrefix} non-video file selected`, { type: file.type });
      alert('Please select a valid video file.');
      return;
    }

    // console.log(`${logPrefix} file selected (no compression yet)`, {
    //   name: file.name,
    //   type: file.type,
    //   size: file.size,
    // });
 
    // Clean up previous preview/file
    const previousPreview = localPreview;
    const previousFile = localFile;
    
    // Detect iOS for special handling
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || 
                  (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
   
    // Clear video element ref first
    if (videoElementRef.current) {
      const videoEl = videoElementRef.current;
      // On iOS, ensure video is properly stopped and cleared
      if (isIOS) {
        videoEl.pause();
        videoEl.src = '';
        videoEl.load();
      }
      videoElementRef.current = null;
    }
   
    // Clear state first to trigger cleanup
    // The useEffect cleanup will handle URL revocation with proper timing
    if (localPreview) {
      setLocalPreview(null);
    }
    if (localFile) {
      setLocalFile(null);
    }
    setSelectedFiles?.((prev) => {
      if (!prev || !prev[questionId]) return prev;
      const next = { ...prev };
      delete next[questionId];
      return next;
    });
    setUploadProgressFraction(0);
    clearVideoUploadSession(questionId).catch(() => {});
   
    // On iOS, add a small delay to ensure previous video element is fully cleaned up
    const setPreview = () => {
      // Show original file in preview immediately (no compression)
      const previewUrl = URL.createObjectURL(file);
      
      // Ensure we're not setting a stale preview
      setLocalFile((prev) => {
        // console.log(`${logPrefix} setLocalFile -> original`, { prev, next: file });
        return file;
      });
      setLocalPreview((prev) => {
        // Only set if we don't have a newer preview already
        if (prev && prev !== previousPreview) {
          // console.warn(`${logPrefix} skipping preview update, newer preview exists`, { prev, newPreview: previewUrl });
          URL.revokeObjectURL(previewUrl);
          return prev;
        }
        // console.log(`${logPrefix} setLocalPreview -> original`, { prev, next: previewUrl });
        return previewUrl;
      });
      setSelectedFiles?.((prev) => {
        const next = { ...prev, [questionId]: file };
        // console.log(`${logPrefix} setSelectedFiles -> original`, next);
        return next;
      });
      setVideoUploadSuccess?.(prev => {
        const newState = { ...prev };
        delete newState[questionId];
        return newState;
      });
    };
    
    if (isIOS && previousPreview) {
      // Small delay on iOS to ensure cleanup
      setTimeout(setPreview, 150);
    } else {
      setPreview();
    }
  };
 
  // ✅ Samsung Internet-safe gallery picker (no camera)
  const openGalleryPicker = () => {
    if (disabled) return;
    if (typeof onRequestStartRecording === 'function') {
      const ok = onRequestStartRecording(question.question_ID);
      if (!ok) return;
    }
 
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'video/*'; // Gallery only
    input.style.display = 'none';
    document.body.appendChild(input);
 
    input.addEventListener('change', (e) => {
      handleFileChange(e);
      document.body.removeChild(input);
    });
 
    input.click();
  };
 
  useEffect(() => {
    if (!canResumeVideoUpload || typeof onResumeVideoUpload !== 'function') return undefined;
    const handleOnline = () => {
      if (navigator.onLine) {
        onResumeVideoUpload();
      }
    };
    window.addEventListener('online', handleOnline);
    return () => window.removeEventListener('online', handleOnline);
  }, [canResumeVideoUpload, onResumeVideoUpload]);

  // Parent progress is source of truth (esp. auto-resume). Never Math.max with
  // stale local values or a prior upload's 100% sticks after delete + reupload.
  useEffect(() => {
    if (
      (submitting || videoUploadScreenSuccess || canResumeVideoUpload) &&
      Number.isFinite(savedUploadProgress)
    ) {
      setUploadProgressFraction(Math.min(1, Math.max(0, savedUploadProgress)));
    }
  }, [submitting, videoUploadScreenSuccess, canResumeVideoUpload, savedUploadProgress]);

  // When resume UI closes (delete / new upload), drop any stale local 100%.
  useEffect(() => {
    if (!canResumeVideoUpload && !submitting && !videoUploadScreenSuccess) {
      setUploadProgressFraction(Number.isFinite(savedUploadProgress) ? savedUploadProgress : 0);
    }
  }, [canResumeVideoUpload, submitting, videoUploadScreenSuccess, savedUploadProgress]);

  const activeConnectionStage =
    connectionMessageStage != null ? connectionMessageStage : checkFileApiMessageStage;
  const networkErrorTextStyle = {
    color: '#f44336',
    fontSize: '12px',
    marginTop: '4px',
  };

  // === UI Logic ===
  // console.log(`${logPrefix} render branch check`, {
  //   videoUploadScreenSuccess,
  //   submitting,
  //   hasLocalPreview: !!localPreview,
  //   hasCurrentVideoUrl: !!currentVideoUrl,
  // });
  let content;
  // Once CheckFileInS3 succeeds (incl. after auto-resume), prefer success UI.
  const showUploadedSuccess =
    Boolean(preAssignedVideoUrl || successMsg || currentVideoUrl) &&
    !videoUploadScreenSuccess &&
    !submitting &&
    activeConnectionStage == null &&
    // Avoid treating local-only blob preview as "uploaded" before CheckFileInS3.
    Boolean(preAssignedVideoUrl || successMsg);

  if (showUploadedSuccess) {
    content = (
      <>
        <SuccessRow>
          <SuccessMessage>&#10003; Video uploaded successfully.</SuccessMessage>
          <DeleteButton onClick={handleDelete} title="Delete video" disabled={deleting || disabled}>
            <FaTrash />
          </DeleteButton>
        </SuccessRow>
      </>
    );
  } else if (videoUploadScreenSuccess || submitting || activeConnectionStage != null) {
    const activeUploadStage = uploadStageProp || uploadStage;
    const activeRemainingSeconds = Number.isFinite(remainingUploadSecondsProp)
      ? remainingUploadSecondsProp
      : remainingUploadSeconds;
    const remainingTimeLabel = formatRemainingUploadTime(activeRemainingSeconds);
    const displayProgress = Number.isFinite(savedUploadProgress)
      ? savedUploadProgress
      : Number.isFinite(uploadProgressFraction)
        ? uploadProgressFraction
        : 0;
    const uploadPercent = Math.min(100, Math.round(Math.min(1, Math.max(0, displayProgress)) * 100));
    // console.log(`${logPrefix} rendering upload spinner state`);
    content = (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: 20 }}>
        <div style={{ marginBottom: '10px' }}>
          <div
            style={{
              width: '60px',
              height: '60px',
              borderRadius: '50%',
              background: `conic-gradient(#9ebe21 ${uploadPercent * 3.6}deg, #f3f3f3 0deg)`,
              margin: '0 auto',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <div
              style={{
                width: '52px',
                height: '52px',
                borderRadius: '50%',
                background: '#fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '14px',
                color: '#666',
              }}
            >
              {uploadPercent}%
            </div>
          </div>
        </div>
        {activeConnectionStage == null ? (
          <>
            <p style={{ marginTop: 10, color: '#9EBE21', fontWeight: 500 }}>
              {activeUploadStage === 'preparing'
                ? 'Preparing video'
                : activeUploadStage === 'confirming'
                  ? 'video confirming'
                  : 'Video uploading'}
            </p>
            {activeUploadStage === 'uploading' && remainingTimeLabel && (
              <p style={{ marginTop: 4, color: '#666', fontSize: '13px' }}>
                {remainingTimeLabel}
              </p>
            )}
          </>
        ) : (
          <p style={{ marginTop: 10, color: 'red', fontWeight: 500 }}>
            {activeConnectionStage === 1 && 'Your connection is down...'}
            {activeConnectionStage === 2 && 'It is taking longer than usual...'}
            {activeConnectionStage === 3 && 'Video upload failed'}
          </p>
        )}
      </div>
    );
  } else if (canResumeVideoUpload) {
    const resumePercent = Math.min(
      100,
      Math.round((Number.isFinite(savedUploadProgress) ? savedUploadProgress : 0) * 100)
    );
    const resumeError = uploadErrorMessage || uploadActionError;
    content = (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: 20, width: '100%' }}>
        {(localPreview || currentVideoUrl) && (
          <MediaPreview>
            <video
              controls
              src={localPreview || currentVideoUrl}
              preload="metadata"
              playsInline
              webkit-playsinline="true"
            />
          </MediaPreview>
        )}
        <p style={{ color: '#666', marginBottom: 8 }}>Upload paused at {resumePercent}%</p>
        {resumeError ? (
          <p style={{ ...networkErrorTextStyle, marginBottom: 16 }}>{resumeError}</p>
        ) : (
          <div style={{ marginBottom: 16 }} />
        )}
        <div style={{ display: 'flex', gap: 15, justifyContent: 'center', width: '100%' }}>
          <UploadButton
            type="button"
            onClick={() => {
              if (!navigator.onLine) {
                showTempUploadErrorMessage(
                  'No connection. Please connect to the internet and try again.'
                );
                return;
              }
              clearUploadErrorMessage();
              if (typeof onClearUploadActionError === 'function') onClearUploadActionError();
              if (typeof onResumeVideoUpload === 'function') onResumeVideoUpload();
            }}
            style={{
              background: '#9EBE21',
              color: '#fff',
              borderColor: '#9EBE21',
            }}
          >
            Retry
          </UploadButton>
          <UploadButton
            type="button"
            onClick={() => {
              if (!navigator.onLine) {
                showTempUploadErrorMessage(
                  'No connection. Please connect to the internet and try again.'
                );
                return;
              }
              clearUploadErrorMessage();
              setUploadProgressFraction(0);
              if (typeof onClearUploadActionError === 'function') onClearUploadActionError();
              if (typeof onDeleteIncompleteVideoUpload === 'function') {
                onDeleteIncompleteVideoUpload();
              }
            }}
            style={{
              background: '#fff',
              color: '#9EBE21',
              borderColor: '#9EBE21',
            }}
          >
            Delete
          </UploadButton>
        </div>
      </div>
    );
  } else if (localPreview) {
    content = (
      <>
        <MediaPreview>
          <video
            key={localPreview}
            controls
            src={localPreview}
            preload="metadata"
            playsInline
            webkit-playsinline="true"
            onError={(e) => {
              const currentUrl = currentPreviewRef.current;
              if (currentUrl !== localPreview) {
                // console.warn(`${logPrefix} video load error for stale URL (ignored)`, {
                //   localPreview,
                //   currentUrl,
                //   error: e
                // });
                return;
              }
              const videoElement = e.target;
              // console.error(`${logPrefix} video load error`, {
              //   localPreview,
              //   error: e,
              //   videoError: videoElement?.error,
              //   errorCode: videoElement?.error?.code,
              //   errorMessage: videoElement?.error?.message
              // });
              // Try to recover by clearing and recreating if we still have the file
              if (localFile) {
                // console.log(`${logPrefix} attempting to recover from video load error`);
                // Revoke old URL first
                if (localPreview) {
                  URL.revokeObjectURL(localPreview);
                }
                const newUrl = URL.createObjectURL(localFile);
                setLocalPreview(newUrl);
              }
            }}
            onLoadedData={(e) => {
              const currentUrl = currentPreviewRef.current;
              const videoElement = e.target;
              if (currentUrl === localPreview) {
                // console.log(`${logPrefix} video loaded successfully`, { 
                //   localPreview,
                //   videoWidth: videoElement.videoWidth,
                //   videoHeight: videoElement.videoHeight,
                //   duration: videoElement.duration
                // });
              } else {
                // console.warn(`${logPrefix} video loaded but URL is stale (ignored)`, {
                //   localPreview,
                //   currentUrl
                // });
              }
            }}
            onLoadStart={() => {
              // console.log(`${logPrefix} video load started`, { localPreview });
            }}
            onCanPlay={() => {
              // console.log(`${logPrefix} video can play`, { localPreview });
            }}
            ref={videoElementRef}
          />
        </MediaPreview>
        {uploadErrorMessage && (
          <p
            style={{
              color: '#f44336',
              fontSize: '12px',
              marginTop: '4px',
              marginBottom: '10px',
              textAlign: 'center',
            }}
          >
            {uploadErrorMessage}
          </p>
        )}
        <div style={{ display: 'flex', gap: 15, justifyContent: 'center', width: '100%' }}>
          <UploadButton
            type="button"
            onClick={openGalleryPicker}
            disabled={disabled}
            style={{
              background: '#fff',
              color: '#9EBE21',
              borderColor: '#9EBE21',
              opacity: disabled ? 0.5 : 1,
            }}
          >
            Upload Again
          </UploadButton>
          <UploadButton
            type="button"
            disabled={submitting || disabled}
            onClick={async () => {
              if (!localFile || disabled) return;
              // const connection = navigator.connection;
              // const connectionLooksDown =
              //   Boolean(connection) &&
              //   (connection.metered || (typeof connection.downlink === 'number' && connection.downlink < 1));
              if (!navigator.onLine) {
                showTempUploadErrorMessage(
                  'Network is down. Please try connecting to another network'
                );
                return;
              }

              if (typeof onRequestStartRecording === 'function') {
                const ok = onRequestStartRecording(question.question_ID);
                if (!ok) return;
              }
              
              setSubmitting(true);
              setCheckFileApiMessageStage(null);
              setUploadProgressFraction(0);
              setRemainingUploadSeconds(null);
              setUploadStage('preparing');
              clearUploadErrorMessage();
              if (typeof onClearUploadActionError === 'function') onClearUploadActionError();

              try {
                // console.log(`${logPrefix} starting upload`, {
                //   fileToUpload: localFile.name,
                //   fileSize: localFile.size,
                // });
                await uploadVideo(
                  questionId,
                  15,
                  localFile,
                  ({ attempt }) => {
                    setCheckFileApiMessageStage(attempt);
                  },
                  (fraction, meta) => {
                    // Always follow current upload fraction; do not keep a previous upload's 100%.
                    setUploadProgressFraction(Math.min(1, Math.max(0, Number(fraction) || 0)));
                    if (meta?.stage) setUploadStage(meta.stage);
                    if (meta?.stage === 'uploading') {
                      if (Number.isFinite(meta.remainingSeconds)) {
                        setRemainingUploadSeconds(meta.remainingSeconds);
                      }
                    } else {
                      setRemainingUploadSeconds(null);
                    }
                  }
                );

                // console.log(`${logPrefix} upload successful`);
                setCheckFileApiMessageStage(null);
                setRemainingUploadSeconds(null);
              } catch (error) {
                // console.error(`${logPrefix} upload failed:`, error);
                const failMessage =
                  error?.message ||
                  (error?.isNetworkError
                    ? 'Network error. Please check your connection and try again.'
                    : 'Video upload failed. Please try again.');
                // Parent already showed the 3 connection messages (+ 5s failed text).
                setSubmitting(false);
                setCheckFileApiMessageStage(null);
                if (!error?.connectionMessagesShown) {
                  showTempUploadErrorMessage(failMessage);
                }
                if (Number.isFinite(savedUploadProgress)) {
                  setUploadProgressFraction(Math.min(1, Math.max(0, savedUploadProgress)));
                }
              }
            }}
            style={{
              background: '#9EBE21',
              color: '#fff',
              borderColor: '#9EBE21',
              opacity: disabled ? 0.5 : 1,
            }}
          >
            Submit
          </UploadButton>
        </div>
      </>
    );
  } else {
    // console.log(`${logPrefix} rendering initial upload state`);
    content = (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <UploadButton
          type="button"
          onClick={openGalleryPicker}
          disabled={disabled}
          style={{
            opacity: disabled ? 0.5 : 1,
            cursor: disabled ? 'not-allowed' : 'pointer',
          }}
        >
          <MdUpload style={{ height: '24px', width: '24px' }} />
        </UploadButton>
        <p style={{ marginTop: 8, fontSize: 14, color: '#333' }}>Upload</p>
      </div>
    );
  }
 
  return (
    <VideoUploadContainer style={{ opacity: disabled ? 0.5 : 1 }}>
      {content}
    </VideoUploadContainer>
  );
};
 
export default VideoResponseUpload;